import React, { useCallback, useState } from "react";

import {
  ActionsMenu,
  MENU_PORTAL_ATTRIBUTE,
  MenuItem,
  ActionMenuDivider,
  ActionsMenuTrigger,
} from "ui/actions-menu";
import { QuickTip } from "ui/quick-tip";
import { useHandleEditorOpen } from "containers/admin/clients/touchpoint/components/builder/use-handle-editor-open";
import { getTouchpointActionGates } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/get-touchpoint-action-gates";
import { getDisabledActionMessage } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/disabled-action-tooltip/disabled-action-tooltip-message";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";

import { useClaimVersionLock } from "hooks/api/use-touchpoint-versions";
import { useGuide } from "hooks/use-guide";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import { useFeatureFlag } from "contexts/feature-flag-context";

import { TouchpointStatuses } from "types";
import { TouchpointType } from "types/touchpoint";
import { isEmail } from "types/typeguards";
import { PreviewTabHeaderMenuProps } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/preview-tab-header/preview-tab-header-menu/preview-tab-header-menu.types";

import { GUIDE_IDS } from "constants/guide-ids";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { getActionTouchpointTypeLabel } from "utilities";
import Icons from "assets";

const EMAIL_REWRITER_TIP_TITLE = "Meet Email rewriter";
const EMAIL_REWRITER_TIP_BODY =
  "Refine your draft without starting from scratch. Adjust tone, rephrase sections, or refresh entire email in a few clicks.";

const REWRITE_EMAIL_MENU_TIP_TITLE = "Rewrite email instantly";
const REWRITE_EMAIL_MENU_TIP_BODY =
  "Select this option to effortlessly rewrite your email draft with Apollo. The entire draft will update, and your previous version will be safely archived.";

const PreviewTabHeaderMenu: React.FC<PreviewTabHeaderMenuProps> = ({
  isActionsMenuOpen,
  setIsActionsMenuOpen,
  selectedVersion,
  confirmPublishModal,
  approveAsClientModal,
  setIsRejectAsClientModalOpen,
  cloneTouchpointModal,
  setIsCloneTemplateOpen,
  setIsAddToBatchModalOpen,
  setIsEditProgramTemplateNameModalOpen,
  editVersionModal,
  confirmArchiveModal,
  setIsUpdateClientThemeModalOpen,
  setIsRefineEmailContentModalOpen,
  setCreateTouchpointFromTemplateType,
  setIsCreateParentTouchpointModalOpen,
  handleTouchpointStatusSending,
  verifyLockAndProceed,
  handleCreateDraftFromVersion,
  // Shared calculations from parent
  selectedVersionIsPublished,
  touchpointIsEmail,
  isLockedByAnotherUser,
  isApproved,
  isClientAdmin,
  isClientUser,
  actionItemCount,
}) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const isProgramBriefsEnabled = useFeatureFlag("program-briefs");
  const claimLockMutation = useClaimVersionLock({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });
  const { isDismissed: isEmailRewriterTipDismissed, dismiss: dismissEmailRewriterTip } = useGuide(
    GUIDE_IDS.CREATIVE_PREVIEW_EMAIL_REWRITER,
  );
  const { isDismissed: isRewriteEmailMenuTipDismissed, dismiss: dismissRewriteEmailMenuTip } =
    useGuide(GUIDE_IDS.ACTIONS_MENU_REWRITE_EMAIL);

  // State, not a ref: the tip's `Popper` cannot position until a render has seen the anchor element.
  const [rewriteItemEl, setRewriteItemEl] = useState<HTMLDivElement | null>(null);
  const [actionsTriggerEl, setActionsTriggerEl] = useState<HTMLButtonElement | null>(null);

  const isTemplate = currentCampaign?.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;

  const { handleEditorOpen } = useHandleEditorOpen({
    selectedVersion,
    latestVersion: selectedVersion,
  });

  const selectedVersionCanBeArchived = [
    TouchpointStatuses.DRAFT,
    TouchpointStatuses.SYSTEM_DRAFT,
  ].includes(selectedVersion?.status as TouchpointStatuses);

  const getClientVersionStatuses = useCallback((status?: string) => {
    if (!status) return null;
    return { status };
  }, []);

  const clientVersionStatuses = getClientVersionStatuses(selectedVersion?.status);
  const notArchivedDraft = clientVersionStatuses?.status !== TouchpointStatuses.ARCHIVED_DRAFT;
  const notArchivedClientReview =
    clientVersionStatuses?.status !== TouchpointStatuses.ARCHIVED_CLIENT_REVIEW;
  const isInReview = clientVersionStatuses?.status === TouchpointStatuses.CLIENT_REVIEW;
  const canShowEmailRewriter = isProgramBriefsEnabled && touchpointIsEmail && !isInReview;
  const isDraftOrSystemDraft = [
    TouchpointStatuses.DRAFT,
    TouchpointStatuses.SYSTEM_DRAFT,
  ].includes(selectedVersion?.status as TouchpointStatuses);
  const isVersionInBatch = !!selectedVersion?.touchpointBatchId;
  const isPublishedTemplate = isTemplate && selectedVersionIsPublished;
  const isClientApproved = isApproved || isPublishedTemplate;
  const canApproveOrRejectAsClient =
    !isClientAdmin &&
    !isClientUser &&
    !isClientApproved &&
    ![TouchpointStatuses.ARCHIVED_DRAFT, TouchpointStatuses.REJECTED].includes(
      clientVersionStatuses?.status as TouchpointStatuses,
    );

  const canCreateParentTouchpoint =
    isEmail(selectedVersion) &&
    currentTouchpoint.populationType === "STUDENT" &&
    !(selectedVersion as EmailAttributes)?.emptyContent;

  const canApplyTheme =
    isEmail(selectedVersion) &&
    !(selectedVersion as EmailAttributes)?.emptyContent &&
    (selectedVersion as EmailAttributes)?.applyClientTheme === true;
  const touchpointIsOther = selectedVersion?.type === TouchpointType.OTHER;

  const {
    isBlockedByLock,
    isBlockedByInvalidFields,
    isBlockedByOpenActionItems,
    isPublishDisabled,
  } = getTouchpointActionGates({
    selectedVersion,
    isLockedByAnotherUser,
    isTemplate,
    isApproved,
    actionItemCount,
  });

  const canSendToClientBasic =
    !isClientApproved && notArchivedDraft && notArchivedClientReview && !isInReview;
  const canSendToClientApproved = isClientApproved;
  const hasReviewOrArchiveActions =
    canSendToClientBasic ||
    canApproveOrRejectAsClient ||
    canSendToClientApproved ||
    selectedVersionCanBeArchived;

  const handleEditVersionDetails = useCallback(() => {
    verifyLockAndProceed(() => {
      claimLockMutation.mutate(selectedVersion?.id as string);
      editVersionModal.onOpen();
    });
  }, [verifyLockAndProceed, editVersionModal, claimLockMutation, selectedVersion?.id]);

  const handleSendToClient = useCallback(() => {
    verifyLockAndProceed(() => handleTouchpointStatusSending(TouchpointStatuses.CLIENT_REVIEW));
  }, [verifyLockAndProceed, handleTouchpointStatusSending]);

  const handleOpenPublishModal = useCallback(() => {
    verifyLockAndProceed(() => confirmPublishModal.onOpen());
  }, [verifyLockAndProceed, confirmPublishModal]);

  const handleOpenApproveAsClientModal = useCallback(() => {
    verifyLockAndProceed(() => approveAsClientModal.onOpen());
  }, [verifyLockAndProceed, approveAsClientModal]);

  const handleOpenRejectAsClientModal = useCallback(() => {
    verifyLockAndProceed(() => setIsRejectAsClientModalOpen(true));
  }, [verifyLockAndProceed, setIsRejectAsClientModalOpen]);

  const handleArchiveDraftAction = useCallback(() => {
    verifyLockAndProceed(() => confirmArchiveModal.onOpen());
  }, [verifyLockAndProceed, confirmArchiveModal]);

  const handleCloneTouchpoint = useCallback(() => {
    verifyLockAndProceed(() => cloneTouchpointModal.onOpen());
  }, [verifyLockAndProceed, cloneTouchpointModal]);

  const handleCloneTemplate = useCallback(() => {
    verifyLockAndProceed(() => setIsCloneTemplateOpen(true));
  }, [verifyLockAndProceed, setIsCloneTemplateOpen]);

  const handleCreateParentTouchpoint = useCallback(() => {
    verifyLockAndProceed(() => setIsCreateParentTouchpointModalOpen(true));
  }, [verifyLockAndProceed, setIsCreateParentTouchpointModalOpen]);

  const handleOpenUpdateClientThemeModal = useCallback(() => {
    verifyLockAndProceed(() => setIsUpdateClientThemeModalOpen(true));
  }, [verifyLockAndProceed, setIsUpdateClientThemeModalOpen]);

  const handleCreateEmailFromTemplate = useCallback(() => {
    verifyLockAndProceed(() => setCreateTouchpointFromTemplateType(TouchpointType.EMAIL));
  }, [verifyLockAndProceed, setCreateTouchpointFromTemplateType]);

  const handleCreateNonEmailFromTemplate = useCallback(() => {
    const type = touchpointIsOther ? TouchpointType.OTHER : TouchpointType.LANDING_PAGE;

    verifyLockAndProceed(() => setCreateTouchpointFromTemplateType(type));
  }, [verifyLockAndProceed, setCreateTouchpointFromTemplateType, touchpointIsOther]);

  const handleEditProgramTemplateName = useCallback(() => {
    verifyLockAndProceed(() => setIsEditProgramTemplateNameModalOpen(true));
  }, [verifyLockAndProceed, setIsEditProgramTemplateNameModalOpen]);

  const handleMoveToBatch = useCallback(() => {
    setIsAddToBatchModalOpen(true);
  }, [setIsAddToBatchModalOpen]);

  const handleOpenContentSettings = useCallback(() => {
    verifyLockAndProceed(() => handleEditorOpen("content-settings"));
  }, [verifyLockAndProceed, handleEditorOpen]);

  const handleActionsMenuOpen = useCallback(() => {
    if (canShowEmailRewriter) {
      dismissEmailRewriterTip();
    }
    setIsActionsMenuOpen(true);
  }, [canShowEmailRewriter, dismissEmailRewriterTip, setIsActionsMenuOpen]);

  const handleActionsMenuClose = useCallback(() => {
    setIsActionsMenuOpen(false);
  }, [setIsActionsMenuOpen]);

  const handleEmailRewriter = useCallback(() => {
    dismissEmailRewriterTip();
    dismissRewriteEmailMenuTip();
    verifyLockAndProceed(() => {
      setIsActionsMenuOpen(false);
      setIsRefineEmailContentModalOpen(true);
    });
  }, [
    dismissEmailRewriterTip,
    dismissRewriteEmailMenuTip,
    verifyLockAndProceed,
    setIsActionsMenuOpen,
    setIsRefineEmailContentModalOpen,
  ]);

  // Non-publish menu items are gated by lock only; scope the helper accordingly.
  const getActionTooltip = () =>
    getDisabledActionMessage({
      isBlockedByLock,
      isBlockedByInvalidFields: false,
      isBlockedByOpenActionItems: false,
      selectedVersion,
      handleOpenContentSettings,
    });

  const getPublishItemTooltip = () =>
    getDisabledActionMessage({
      isBlockedByLock,
      isBlockedByInvalidFields,
      isBlockedByOpenActionItems,
      selectedVersion,
      handleOpenContentSettings,
    });

  const renderCreateDraftItem = () => (
    <MenuItem
      icon={<Icons.NewDocument />}
      label="Create draft from this version"
      onClick={handleCreateDraftFromVersion}
      disabled={isBlockedByLock}
      tooltip={getActionTooltip()}
    />
  );

  const showRewriteEmailMenuTip =
    canShowEmailRewriter && isActionsMenuOpen && !isRewriteEmailMenuTipDismissed;

  const renderEmailRewriterItem = () => {
    if (!canShowEmailRewriter) return null;

    return (
      <div
        ref={setRewriteItemEl}
        className={showRewriteEmailMenuTip ? "rounded-md ring-2 ring-aqua-600" : undefined}>
        <MenuItem
          icon={<Icons.CampaignWriter />}
          label="Rewrite email"
          onClick={handleEmailRewriter}
          disabled={isBlockedByLock}
          tooltip={getActionTooltip()}
        />

        {/*
          The row sits inside `MenuPanel`, a scroll container that would clip anything positioned past
          its edge; `QuickTip` portals out of it. The attribute keeps a click on the tip from closing the
          menu it points into.
        */}
        <QuickTip
          {...{ [MENU_PORTAL_ATTRIBUTE]: true }}
          open={showRewriteEmailMenuTip}
          anchorEl={rewriteItemEl}
          placement="left-start"
          title={REWRITE_EMAIL_MENU_TIP_TITLE}
          body={REWRITE_EMAIL_MENU_TIP_BODY}
          onClose={dismissRewriteEmailMenuTip}
          onConfirm={dismissRewriteEmailMenuTip}
        />
      </div>
    );
  };

  const renderApplyThemeItem = () => {
    if (!canApplyTheme) return null;

    return (
      <MenuItem
        icon={<Icons.Theme />}
        label="Apply theme"
        onClick={handleOpenUpdateClientThemeModal}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderCloneItem = () => {
    return (
      <MenuItem
        icon={<Icons.Clone />}
        label={
          isTemplate
            ? "Clone template"
            : `Clone ${getActionTouchpointTypeLabel(selectedVersion?.type ?? TouchpointType.OTHER).toLowerCase()}`
        }
        onClick={isTemplate ? handleCloneTemplate : handleCloneTouchpoint}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderCreateParentTouchpointItem = () => {
    if (!canCreateParentTouchpoint) return null;

    return (
      <MenuItem
        icon={<Icons.Parent />}
        label="Create parent email"
        onClick={handleCreateParentTouchpoint}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  // Template-specific actions
  const renderCreateEmailFromTemplateItem = () => {
    if (!isTemplate || !touchpointIsEmail || !isClientApproved) return null;

    return (
      <MenuItem
        icon={<Icons.Email />}
        label="Create email from template"
        onClick={handleCreateEmailFromTemplate}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderCreateNonEmailFromTemplateItem = () => {
    if (!isTemplate || touchpointIsEmail || !isClientApproved) return null;

    const Icon = touchpointIsOther ? Icons.Other : Icons.LandingPage;
    const label = touchpointIsOther
      ? "Create touchpoint from template"
      : "Create web page from template";

    return (
      <MenuItem
        icon={<Icon />}
        label={label}
        onClick={handleCreateNonEmailFromTemplate}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderMoveToBatchItem = () => {
    const canChangeBatchFromMenu = isInReview || (isDraftOrSystemDraft && isVersionInBatch);
    if (!isBatchManagementEnabled || !canChangeBatchFromMenu) return null;

    const batchMenuLabel = isVersionInBatch ? "Change batch" : "Add to batch";

    return (
      <MenuItem
        icon={<Icons.Layers />}
        label={batchMenuLabel}
        onClick={handleMoveToBatch}
      />
    );
  };

  const renderEditProgramTemplateNameItem = () => {
    if (!isTemplate) return null;

    return (
      <MenuItem
        icon={<Icons.Edit />}
        label="Edit template name"
        onClick={handleEditProgramTemplateName}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderEditVersionDetailsItem = () => (
    <MenuItem
      icon={<Icons.Document />}
      label="Edit version details"
      onClick={handleEditVersionDetails}
      disabled={isBlockedByLock}
      tooltip={getActionTooltip()}
    />
  );

  const renderSendToClientItem = () => {
    if (!canSendToClientBasic) return null;

    return (
      <MenuItem
        icon={<Icons.Airplane />}
        label="Send to client"
        onClick={handleSendToClient}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderApproveAsClientItem = () => {
    if (!canApproveOrRejectAsClient) return null;

    return (
      <MenuItem
        icon={<Icons.CheckCircle />}
        label="Approve as client"
        onClick={handleOpenApproveAsClientModal}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderRejectAsClientItem = () => {
    if (!canApproveOrRejectAsClient) return null;

    return (
      <MenuItem
        icon={<Icons.Cancel />}
        label="Reject as client"
        onClick={handleOpenRejectAsClientModal}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderSendToClientApprovedItem = () => {
    if (!canSendToClientApproved) return null;

    return (
      <MenuItem
        icon={<Icons.Airplane />}
        label="Send to client"
        onClick={handleSendToClient}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderArchiveDraftItem = () => {
    if (!selectedVersionCanBeArchived) return null;

    return (
      <MenuItem
        icon={<Icons.Archive />}
        label="Archive draft"
        color="error"
        onClick={handleArchiveDraftAction}
        disabled={isBlockedByLock}
        tooltip={getActionTooltip()}
      />
    );
  };

  const renderPublishItem = () => (
    <MenuItem
      icon={<Icons.Upload />}
      label={touchpointIsEmail ? "Publish to Iterable" : "Publish"}
      onClick={handleOpenPublishModal}
      disabled={isPublishDisabled}
      tooltip={getPublishItemTooltip()}
    />
  );

  const renderPublishedVersionItems = () => (
    <>
      {renderCreateDraftItem()}
      {canShowEmailRewriter && <ActionMenuDivider />}
      {renderEmailRewriterItem()}
      {renderCreateParentTouchpointItem()}
    </>
  );

  const renderUnpublishedVersionItems = () => (
    <>
      {renderCreateDraftItem()}
      {renderApplyThemeItem()}
      {renderCloneItem()}
      {renderCreateEmailFromTemplateItem()}
      {renderCreateNonEmailFromTemplateItem()}

      {(canShowEmailRewriter || canCreateParentTouchpoint) && <ActionMenuDivider />}
      {renderEmailRewriterItem()}
      {renderCreateParentTouchpointItem()}

      <ActionMenuDivider />

      {renderMoveToBatchItem()}
      {renderEditProgramTemplateNameItem()}
      {renderEditVersionDetailsItem()}

      {hasReviewOrArchiveActions && (
        <>
          <ActionMenuDivider />
          {renderSendToClientItem()}
          {renderApproveAsClientItem()}
          {renderRejectAsClientItem()}
          {renderSendToClientApprovedItem()}
          {renderArchiveDraftItem()}
        </>
      )}

      {!isTemplate && !touchpointIsOther && (
        <>
          <ActionMenuDivider />
          {renderPublishItem()}
        </>
      )}
    </>
  );

  const showEmailRewriterTip =
    canShowEmailRewriter && !isEmailRewriterTipDismissed && !isActionsMenuOpen;

  return (
    <div className="relative">
      <ActionsMenu
        trigger={<ActionsMenuTrigger size="large" ref={setActionsTriggerEl} />}
        open={isActionsMenuOpen}
        onOpen={handleActionsMenuOpen}
        onClose={handleActionsMenuClose}
        itemName="creative version"
        menuId="creative-version-actions-menu">
        {selectedVersionIsPublished && !isTemplate
          ? renderPublishedVersionItems()
          : renderUnpublishedVersionItems()}
      </ActionsMenu>

      <QuickTip
        open={showEmailRewriterTip}
        anchorEl={actionsTriggerEl}
        placement="bottom-end"
        title={EMAIL_REWRITER_TIP_TITLE}
        body={EMAIL_REWRITER_TIP_BODY}
        onClose={dismissEmailRewriterTip}
        onConfirm={dismissEmailRewriterTip}
      />
    </div>
  );
};

export default PreviewTabHeaderMenu;
